/**
 * De omloop van de hal: één klok voor alles wat er beweegt, en één periode
 * waarin alles precies rond is (22 oktober, de zweefronde als film).
 *
 * Op het hoofdscherm speelt een film van de zweefronde die Remotion offline
 * uit deze zelfde scène rendert (`remotion/`, docs/zweeffilm.md). Bij een tik
 * neemt het realtime-3D het over, en terug op het hoofdscherm de film weer.
 * Dat gaat alleen zonder sprong als beeld τ van de film en de hal op tijd τ
 * hetzelfde zijn. Daarvoor twee dingen:
 *
 *  1. **Eén klok.** Elke truck, de pendel, de deuren, de zwaailichten, het
 *     stof en de camera lezen `halklok.t` in plaats van een eigen klok die
 *     per frame optelt. Een eigen klok is niet terug te zetten; deze wel: de
 *     film zet hem per beeld, de overname zet hem op de tijd van de film.
 *  2. **Alles rond in `OMLOOP` seconden.** Elke rondgang past een geheel
 *     aantal keer in de omloop, elke frequentie draait een geheel aantal keer
 *     rond. Dan is de hal op τ + OMLOOP dezelfde als op τ: de film loopt
 *     naadloos, en het realtime-3D past op de film, ook na een bezoek dat
 *     over het einde van de omloop heen liep.
 *
 * De rondgangen komen daar door hun wachttijden te rekken of in te korten,
 * nooit door sneller te rijden: de snelheden blijven die van het typeblad
 * (`tempo.ts`). 245 s en niet de 210 van eerst: daarbij moest de C-MATIC 25 s
 * onder A wachten, nu wijkt geen rondgang meer dan 2,5 s af van zijn eigen
 * tempo. De camera zweeft daardoor 15 % trager.
 *
 * Puur, zonder imports: `scripts/controleer-bewegingen.mjs` toetst hiermee.
 */

/** Omlooptijd van de hal in seconden: de zweefronde, en de lengte van de film. */
export const OMLOOP = 245

/**
 * De klok van de hal, in seconden. `Halklok` (in `Magazijn.tsx`) telt hem op
 * met de snelheid uit de Studio; staat `extern`, dan zet iemand anders hem —
 * de film in Remotion, per beeld.
 */
export const halklok = { t: 0, extern: false }

/** Hoeksnelheid (rad/s) → de dichtstbijzijnde met een geheel aantal omwentelingen per omloop, minstens één. */
export function rondOm(w: number): number {
  const n = Math.max(1, Math.round((Math.abs(w) * OMLOOP) / (2 * Math.PI)))
  return (Math.sign(w) || 1) * (2 * Math.PI * n) / OMLOOP
}

/**
 * Frequentie (1/s) → de dichtstbijzijnde waarbij `t × f` een geheel aantal
 * keer `stappen` doorloopt per omloop. Voor wat telt in plaats van draait:
 * `(t × f) % 1`, of `floor(t × f) % 5`.
 */
export function rondHz(f: number, stappen = 1): number {
  const n = Math.max(1, Math.round((f * OMLOOP) / stappen))
  return (n * stappen) / OMLOOP
}

/**
 * Nieuwe wachttijden voor een rondgang van `duur` seconden waarin `wachten`
 * zitten, zodat de rondgang een geheel aantal keer in de omloop past. Gekozen
 * wordt de passende rondgang die het dichtst bij de oorspronkelijke ligt.
 * Erbij gaat gelijk over de wachttijden; eraf naar wat elke wachttijd boven
 * `minimum` heeft, zodat er geen korter wordt dan dat. Past de rondgang niet
 * in de omloop, dan blijft alles zoals het was.
 */
export function wachtenOpOmloop(duur: number, wachten: number[], minimum = 1): number[] {
  const totaal = wachten.reduce((s, w) => s + w, 0)
  const ondergrens = duur - totaal + wachten.reduce((s, w) => s + Math.min(w, minimum), 0)
  let beste: number | null = null
  for (let n = 1; OMLOOP / n >= ondergrens; n++) {
    if (beste === null || Math.abs(OMLOOP / n - duur) < Math.abs(beste - duur)) beste = OMLOOP / n
  }
  if (beste === null || wachten.length === 0) return wachten
  const verschil = beste - duur
  if (verschil >= 0) return wachten.map((w) => w + verschil / wachten.length)
  const ruimte = wachten.reduce((s, w) => s + Math.max(0, w - minimum), 0)
  return wachten.map((w) => w + (verschil * Math.max(0, w - minimum)) / ruimte)
}
